"use client";

/**
 * Машины на главной. На входе — статичный автопарк по всем городам вперемешку
 * (из SearchProvider). Выбрал город → автопарк города (/fleet). «Найти машину»
 * → свободные на даты (/search). Пагинация — кнопкой «Показать ещё» по 12
 * (как на сайтах Солнышко). Карточки — родные autopilotrent (CarCard).
 */

import { useEffect, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import CarCard from "./CarCard";
import BannerCard from "./BannerCard";
import InlineBookingPanel from "./InlineBookingPanel";
import { useIsMobile } from "@/lib/use-is-mobile";
import BookingPromptModal from "./BookingPromptModal";
import { useSearch } from "./SearchProvider";
import { useLocale } from "@/lib/locale-context";
import { carPathWithId } from "@/lib/car-slug";
import { interleaveBanners, bannerKind } from "@/lib/banners";
import { track } from "@/lib/metrika";
import type { Car } from "./CarCard";

const PAGE = 12; // шаг кнопки «Показать ещё»

// Скелет карточки авто (мерцание) — показываем во время загрузки автопарка.
function CarSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-[0_4px_12.5px_rgba(0,0,0,0.05)]">
      <div className="h-[200px] w-full animate-pulse bg-[#EEF2F9]" />
      <div className="flex flex-col gap-3 p-4">
        <div className="h-5 w-2/3 animate-pulse rounded bg-[#EEF2F9]" />
        <div className="flex gap-2">
          <div className="h-4 w-14 animate-pulse rounded bg-[#EEF2F9]" />
          <div className="h-4 w-14 animate-pulse rounded bg-[#EEF2F9]" />
          <div className="h-4 w-12 animate-pulse rounded bg-[#EEF2F9]" />
        </div>
        <div className="mt-2 h-8 w-1/3 animate-pulse rounded bg-[#EEF2F9]" />
        <div className="mt-1 flex gap-2">
          <div className="h-11 flex-1 animate-pulse rounded-xl bg-[#EEF2F9]" />
          <div className="h-11 flex-1 animate-pulse rounded-xl bg-[#EEF2F9]" />
        </div>
      </div>
    </div>
  );
}

// маленький чип активного фильтра с крестиком
function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-[13px] font-medium text-primary transition hover:bg-primary/15"
    >
      {label}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
    </button>
  );
}

export default function CarResults() {
  const { cities, cars, status, mode, activeCity, classes, activeClass, filters, lastSearch, setClass, setFilters, pickupGuid, lockedCity, banners } = useSearch();
  const { t, locale } = useLocale();
  const [visible, setVisible] = useState(PAGE);
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null);
  const isMobile = useIsMobile();
  // Блокируем скролл фона, пока открыта мобильная модалка брони.
  useEffect(() => {
    if (!isMobile || !selectedCarId) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isMobile, selectedCarId]);
  const [promptStation, setPromptStation] = useState<string | null>(null); // открыт ли попап «город+даты» (null — закрыт)
  const router = useRouter();

  // Вход в бронь (как в Солнышко handleCarClick):
  // - режим поиска (даты выбраны) → инлайн-блок брони с телефоном в списке;
  // - ГЛАВНАЯ в статике (город/даты не заданы) → попап «город + даты», по нему
  //   редирект на страницу города с подставленными датами;
  // - страница города в статике → страница машины (место подачи в ?delivery=).
  const openCar = (car: Car) => {
    if (mode === "search") {
      setSelectedCarId((cur) => (cur === car.id ? null : car.id));
    } else if (!lockedCity) {
      track("booking_prompt_open", { city: car.station });
      setPromptStation(car.station || "");
    } else {
      const q = pickupGuid ? `?delivery=${encodeURIComponent(pickupGuid)}` : "";
      // Город перед /rent в URL: берём город машины (по станции), иначе выбранный.
      const citySlug = cities.find((c) => c.station === car.station)?.slug || activeCity?.slug || "";
      router.push(carPathWithId(car.title, car.id, citySlug) + q);
    }
  };

  // Сброс пагинации и выбранной карточки при смене режима/города/фильтров.
  useEffect(() => { setVisible(PAGE); setSelectedCarId(null); }, [mode, activeCity, activeClass, filters]);

  const activeClassLabel = activeClass ? classes.find((c) => c.key === activeClass)?.label : undefined;
  const activeCount = (activeClass ? 1 : 0) + (filters.brand ? 1 : 0) + (filters.model ? 1 : 0) + (filters.models?.length ? 1 : 0) + (filters.fuel ? 1 : 0);

  const heading =
    mode === "search"
      ? `${t("results.availableCars")}${activeCity ? ` — ${locale === "en" && (activeCity as { name_en?: string }).name_en ? (activeCity as { name_en?: string }).name_en : activeCity.name}` : ""}`
      : mode === "fleet"
        ? `${t("results.fleet")}${activeCity ? ` — ${locale === "en" && (activeCity as { name_en?: string }).name_en ? (activeCity as { name_en?: string }).name_en : activeCity.name}` : ""}`
        : t("results.fleet");

  // Баннеры занимают ячейки сетки (3 в ряд): число машин до «Показать ещё»
  // подбирается так, чтобы все строки были полными — сначала пробуем добавить
  // машины, если не получилось — убираем.
  const placeable = banners.filter((b) => (mode === "search") === (bannerKind(b) === "promo"));
  let effectiveVisible = Math.min(visible, cars.length);
  if (placeable.length > 0 && effectiveVisible < cars.length) {
    const mixedLen = (n: number) => interleaveBanners(cars.slice(0, n), placeable).length;
    if (mixedLen(effectiveVisible) % 3 !== 0) {
      const candidates: number[] = [];
      for (let d = 1; d <= 4; d++) candidates.push(effectiveVisible + d);
      for (let d = 1; d <= 2; d++) candidates.push(effectiveVisible - d);
      for (const cand of candidates) {
        if (cand < 1 || cand > cars.length) continue;
        if (mixedLen(cand) % 3 === 0 || cand === cars.length) { effectiveVisible = cand; break; }
      }
    }
  }
  const shown = cars.slice(0, effectiveVisible);
  // Карточки (+ промо в общем автопарке) разбиваем на строки по 3 — как у
  // Солнышко: инлайн-блок брони раскрывается ПОД всей строкой, не сдвигая
  // соседние карточки вниз (раньше col-span ломал grid-строку).
  type Node = { key: string; carId?: string; node: ReactNode };
  const carNodes: Node[] = shown.map((car) => ({
    key: car.id,
    carId: car.id,
    node: <CarCard key={car.id} car={car} onOpen={() => openCar(car)} selected={mode === "search" && selectedCarId === car.id} />,
  }));
  // Баннеры в сетке (как на Солнышко): ПРОМО-баннеры показываем ТОЛЬКО в выдаче
  // свободных по датам (mode "search"), обычные (park/link) — в статике
  // (all/fleet). На главной banners = все города, на странице города — свои.
  let nodes: Node[];
  if (placeable.length > 0) {
    nodes = interleaveBanners(carNodes, placeable).map((s) =>
      s.kind === "car"
        ? s.car
        : { key: `banner-${s.key}`, node: <BannerCard key={`banner-${s.key}`} banner={s.banner} /> },
    );
  } else {
    nodes = carNodes;
  }
  const rows: Node[][] = [];
  for (let i = 0; i < nodes.length; i += 3) rows.push(nodes.slice(i, i + 3));
  const selCar = mode === "search" && selectedCarId ? shown.find((c) => c.id === selectedCarId) || null : null;

  const hasMore = effectiveVisible < cars.length;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h3 className="text-[20px] font-semibold text-text">{heading}</h3>
        {status === "ready" && <span className="text-[14px] text-text-muted">{cars.length} {locale === "en" ? "cars" : "авто"}</span>}
        {activeClassLabel && <FilterChip label={activeClassLabel} onClear={() => setClass(null)} />}
        {filters.brand && <FilterChip label={filters.model || filters.brand} onClear={() => setFilters({ ...filters, brand: null, model: null })} />}
        {filters.models?.length ? <FilterChip label={`${t("results.collection")} · ${filters.models.length}`} onClear={() => setFilters({ ...filters, models: null })} /> : null}
        {filters.fuel && <FilterChip label={filters.fuel} onClear={() => setFilters({ ...filters, fuel: null })} />}
        {filters.drive && <FilterChip label={filters.drive === "Полный" ? "4WD" : filters.drive} onClear={() => setFilters({ ...filters, drive: null })} />}
      </div>

      {status === "loading" && cars.length === 0 && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <CarSkeleton key={i} />)}
        </div>
      )}
      {status === "error" && (
        <div className="py-16 text-center text-[16px] text-danger">{locale === "en" ? "Failed to load cars. Please try again." : "Не удалось загрузить машины. Попробуйте ещё раз."}</div>
      )}
      {status === "empty" && (
        <div className="py-16 text-center text-[16px] text-text-muted">
          {mode === "search" ? (locale === "en" ? "No cars available for the selected dates. Change the period." : "На выбранные даты нет свободных машин. Измените период.") : (locale === "en" ? "No cars yet." : "Машин пока нет.")}
        </div>
      )}

      {status === "ready" && cars.length === 0 && activeCount > 0 && (
        <div className="py-16 text-center text-[16px] text-text-muted">
          {locale === "en" ? "No cars match the selected filters." : "Под выбранные фильтры машин нет."}
          <button onClick={() => { track("filter_reset", { city: activeCity?.slug }); setClass(null); setFilters({ brand: null, model: null, fuel: null, drive: null }); }} className="ml-2 cursor-pointer text-primary underline">{locale === "en" ? "Reset filters" : "Сбросить фильтры"}</button>
        </div>
      )}

      {cars.length > 0 && (
        <>
          <div className="flex flex-col gap-6">
            {rows.map((row, ri) => {
              const rowHasSel = !!selCar && row.some((n) => n.carId === selCar.id);
              return (
                <div key={ri}>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {row.map((n) => n.node)}
                  </div>
                  {/* Десктоп: форма брони инлайн под рядом. На мобиле — модалка ниже. */}
                  {rowHasSel && selCar && !isMobile && (
                    <div className="mt-6">
                      <InlineBookingPanel
                        car={selCar}
                        station={lastSearch?.station || activeCity?.station || ""}
                        citySlug={activeCity?.slug || ""}
                        dates={lastSearch}
                        onClose={() => setSelectedCarId(null)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => { track("results_show_more", { city: activeCity?.slug }); setVisible((v) => v + PAGE); }}
                className="cursor-pointer rounded-xl bg-primary px-24 py-4 text-[16px] font-bold text-white transition hover:bg-primary-hover"
              >
                {t("news.showMore")}
              </button>
            </div>
          )}
        </>
      )}

      {/* Мобилка: форма брони на весь экран с кнопкой «Назад» (как на Солнышко). */}
      {isMobile && selCar && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "#fff", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {/* Шапка: кнопка «Назад» (бокс со стрелкой + подпись «Назад») на белом фоне. */}
          <div style={{ position: "sticky", top: 0, zIndex: 2, display: "flex", alignItems: "center", padding: "12px 14px", background: "#fff", borderBottom: "1px solid #EDEDED" }}>
            <button
              type="button"
              onClick={() => setSelectedCarId(null)}
              aria-label={locale === "en" ? "Back" : "Назад"}
              style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "none", border: "none", padding: 0, cursor: "pointer", color: "#0039A6", fontFamily: "inherit" }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, flexShrink: 0, borderRadius: 10, border: "1px solid #EDEDED", background: "#fff" }}>
                <svg viewBox="0 0 24 24" fill="none" width="20" height="20"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
              <span style={{ fontSize: 17, fontWeight: 700, color: "#000" }}>{locale === "en" ? "Back" : "Назад"}</span>
            </button>
          </div>
          <div style={{ padding: "16px 12px 24px", background: "#fff" }}>
            <InlineBookingPanel
              car={selCar}
              station={lastSearch?.station || activeCity?.station || ""}
              citySlug={activeCity?.slug || ""}
              dates={lastSearch}
              flat
            />
          </div>
        </div>
      )}

      {promptStation !== null && (
        <BookingPromptModal
          cities={cities}
          initialStation={promptStation || undefined}
          onClose={() => setPromptStation(null)}
        />
      )}
    </div>
  );
}
